export const notEmpty = <T>(value: T | null | undefined): value is T =>
  value != null;

export const EpochDate = new Date(0);

export const formatMeasuringPointNumber = (number: number) =>
  number.toString().replace(',', '.');

export interface NamedValue<T = any> {
  value: T;
  name: string;
}

export const unique = <T>(items: T[]) => Array.from(new Set(items));

export const uniqueBy = <T>(items: T[], keyFn: (item: T) => string) =>
  Array.from(new Map(items.map((item) => [keyFn(item), item])).values());

export const sort = <T>(items: T[], compareFn?: (a: T, b: T) => number) =>
  [...items].sort(compareFn);

/**
 * Compares two arrays element by element using the provided compare function.
 * Returns the first non-zero comparison result, or the difference in array
 * lengths if all common elements are equal.
 *
 * @param lhs - The first array to compare.
 * @param rhs - The second array to compare.
 * @param compareFn - Function to compare two elements.
 * @returns A negative number if lhs < rhs, positive if lhs > rhs, or 0 if equal.
 */
export const arrayCompare = <T>(
  lhs: T[],
  rhs: T[],
  compareFn: (a: T, b: T) => number
) => {
  for (let i = 0; i < Math.min(lhs.length, rhs.length); i++) {
    const result = compareFn(lhs[i], rhs[i]);
    if (result !== 0) {
      return result;
    }
  }
  return lhs.length - rhs.length;
};
export const numberCompare = (lhs: number, rhs: number) => lhs - rhs;

export const stringLocaleCompare = (
  lhs?: string | null,
  rhs?: string | null
) => {
  if (lhs == null) {
    if (rhs == null) {
      return 0;
    }
    return 1;
  }

  if (rhs == null) {
    return -1;
  }

  return lhs.localeCompare(rhs);
};

export const stringSort = <T>(
  items: T[],
  key: (item: T) => string | undefined | null
) => [...items].sort((lhs, rhs) => stringLocaleCompare(key(lhs), key(rhs)));

export const asArray = <T>(values: T | T[]) =>
  Array.isArray(values) ? values : [values];

export const downloadFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.click();
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  downloadFile(url, filename);
  URL.revokeObjectURL(url);
};

export const downloadResponse = async (
  response: Response,
  defaultFileName: string
) => {
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  downloadFile(url, getFileNameFromResponse(response, defaultFileName));
  URL.revokeObjectURL(url);
};

/**
 * Extracts the filename from the Content-Disposition header of a Response object.
 * @param response The Response object from which to extract the filename.
 * @param defaultName The default filename to return if none is found in the headers.
 * @returns The extracted filename or a default value if not found.
 */
export const getFileNameFromResponse = (
  response: Response,
  defaultName: string
): string => {
  const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;

  const contentDisposition = response.headers.get('content-disposition');

  if (
    contentDisposition != null &&
    contentDisposition.indexOf('attachment') !== -1
  ) {
    const matches = fileNameRegex.exec(contentDisposition);
    if (matches?.[1]) {
      return matches[1].replace(/['"]/g, '').trim();
    }
  }

  return defaultName;
};
